import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import Services from '../components/OurServices';
import LocationMap from '../components/LocationMap';
import {
  HiOutlineSparkles,
  HiOutlineHome,
  HiOutlineMap,
  HiOutlineUsers,
  HiOutlineSun,
  HiOutlineGlobe,
} from 'react-icons/hi';
import { FiArrowUpRight, FiMapPin, FiPhone, FiNavigation } from 'react-icons/fi';
import './Home.css';

const ROOM_CARD_SIZES = '(min-width: 1200px) 25vw, (min-width: 768px) 50vw, 100vw';

const roomCardImage = (name, width, height) => ({
  src: `/images/rooms/${name}-800.webp`,
  srcSet: `/images/rooms/${name}-400.webp 400w, /images/rooms/${name}-800.webp 800w`,
  width,
  height,
});

const HOME_ROOM_CARDS = [
  { ...roomCardImage('master-ac-1', 800, 534), alt: 'Master Room' },
  { ...roomCardImage('ac-couple-1', 800, 534), alt: 'AC Couple Room' },
  { ...roomCardImage('mayatori-1', 800, 534), alt: 'Mayatori Room' },
  { ...roomCardImage('golpotori-1', 800, 1067), alt: 'Golpotori Room' },
];

const Home = () => {
  return (
    <div>
      <Helmet>
        {/* Home Page Specific SEO */}
        <title>Best Hotel in Alikadam, Bandarban | Damtua Inn - Luxury Accommodation & Restaurant</title>
        <meta name="description" content="Discover the best hotel in Alikadam, Bandarban. Damtua Inn offers luxury accommodation, authentic local cuisine, community center, and breathtaking hill views. Book your stay at the top-rated hotel in Alikadam." />
        <meta name="keywords" content="best hotel in Alikadam, top hotel Alikadam, luxury accommodation Bandarban, best place to stay Alikadam, hill station hotel, Alikadam hotels, Bandarban accommodation, luxury hotel near Chittagong" />
        <meta name="author" content="Damtua Inn Hotel & Restaurant" />
        <meta name="publisher" content="Damtua Inn Hotel & Restaurant" />
        <meta name="copyright" content="© 2024 Damtua Inn Hotel & Restaurant" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="geo.region" content="BD-B" />
        <meta name="geo.placename" content="Alikadam, Bandarban" />
        <meta name="geo.position" content="21.6503;92.3060" />
        <meta name="ICBM" content="21.6503, 92.3060" />
        <meta property="og:title" content="Best Hotel in Alikadam, Bandarban | Damtua Inn - Luxury Accommodation & Restaurant" />
        <meta property="og:description" content="Discover the best hotel in Alikadam, Bandarban. Luxury accommodation, authentic local cuisine, and breathtaking hill views." />
        <meta property="og:url" content="https://damtuainn.com/" />
        <meta property="og:type" content="business.hotel" />
        <meta property="og:image" content="https://damtuainn.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Damtua Inn Hotel - Best accommodation in Alikadam, Bandarban" />
        <meta property="og:site_name" content="Damtua Inn Hotel & Restaurant" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Best Hotel in Alikadam, Bandarban | Damtua Inn" />
        <meta name="twitter:description" content="Luxury accommodation, authentic local cuisine, and breathtaking hill views in Bandarban." />
        <meta name="twitter:image" content="https://damtuainn.com/og-image.jpg" />
        <link rel="canonical" href="https://damtuainn.com/" />
        <link rel="alternate" hrefLang="en" href="https://damtuainn.com/" />
        <link rel="alternate" hrefLang="bn" href="https://damtuainn.com/" />
        <link rel="alternate" hrefLang="x-default" href="https://damtuainn.com/" />

        {/* Structured data preserved from prior implementation */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Hotel",
            "name": "Damtua Inn Hotel & Restaurant",
            "alternateName": "The Damtua Inn",
            "image": [
              "https://damtuainn.com/images/restaurant-1.webp",
              "https://damtuainn.com/images/rooms/ac-couple-1.webp",
              "https://damtuainn.com/images/community-1.webp"
            ],
            "description": "Best hotel in Alikadam, Bandarban offering luxury accommodation, authentic local cuisine, and community center facilities with breathtaking hill views",
            "url": "https://damtuainn.com/",
            "telephone": "+8801879917302",
            "email": "thedamtuainn@gmail.com",
            "priceRange": "$$",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Damtua, Alikadam",
              "addressLocality": "Alikadam",
              "addressRegion": "Bandarban",
              "postalCode": "4650",
              "addressCountry": "BD"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 21.6503,
              "longitude": 92.3060
            },
            "starRating": {
              "@type": "Rating",
              "ratingValue": "4.5",
              "bestRating": "5"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.5",
              "reviewCount": "50",
              "bestRating": "5",
              "worstRating": "1"
            },
            "numberOfRooms": 10,
            "checkinTime": "14:00",
            "checkoutTime": "12:00"
          }
          `}
        </script>
      </Helmet>

      <HeroSection />
      <div id="main-content-anchor" />

      {/* 1. Rooms — lead with the product guests are booking */}
      <section className="home-rooms">
        <div className="home-rooms-inner">
          <div className="home-rooms-head">
            <div>
              <span className="eyebrow">Rooms & Suites</span>
              <h2>Eight rooms, each its own quiet world.</h2>
            </div>
            <Link to="/rooms" className="text-link">
              View all rooms <FiArrowUpRight />
            </Link>
          </div>

          <div className="rooms-grid">
            <Link to="/rooms" className="room-card">
              <div className="room-card-media">
                <img
                  src={HOME_ROOM_CARDS[0].src}
                  srcSet={HOME_ROOM_CARDS[0].srcSet}
                  sizes={ROOM_CARD_SIZES}
                  alt={HOME_ROOM_CARDS[0].alt}
                  width={HOME_ROOM_CARDS[0].width}
                  height={HOME_ROOM_CARDS[0].height}
                  loading="lazy"
                  decoding="async"
                />
                <span className="room-card-tag">Air-Conditioned</span>
              </div>
              <div className="room-card-body">
                <h3>Master Room</h3>
                <p>Four-bed suite for families and groups.</p>
                <span className="room-card-meta">Sleeps 4 · Hill view</span>
              </div>
            </Link>

            <Link to="/rooms" className="room-card">
              <div className="room-card-media">
                <img
                  src={HOME_ROOM_CARDS[1].src}
                  srcSet={HOME_ROOM_CARDS[1].srcSet}
                  sizes={ROOM_CARD_SIZES}
                  alt={HOME_ROOM_CARDS[1].alt}
                  width={HOME_ROOM_CARDS[1].width}
                  height={HOME_ROOM_CARDS[1].height}
                  loading="lazy"
                  decoding="async"
                />
                <span className="room-card-tag">Air-Conditioned</span>
              </div>
              <div className="room-card-body">
                <h3>Couple Room</h3>
                <p>A measured double for two, finely calmed.</p>
                <span className="room-card-meta">Sleeps 2 · Hill view</span>
              </div>
            </Link>

            <Link to="/rooms" className="room-card">
              <div className="room-card-media">
                <img
                  src={HOME_ROOM_CARDS[2].src}
                  srcSet={HOME_ROOM_CARDS[2].srcSet}
                  sizes={ROOM_CARD_SIZES}
                  alt={HOME_ROOM_CARDS[2].alt}
                  width={HOME_ROOM_CARDS[2].width}
                  height={HOME_ROOM_CARDS[2].height}
                  loading="lazy"
                  decoding="async"
                />
                <span className="room-card-tag accent">Signature</span>
              </div>
              <div className="room-card-body">
                <h3>Mayatori</h3>
                <p>A signature retreat, intimate and slow.</p>
                <span className="room-card-meta">Sleeps 2 · Forest light</span>
              </div>
            </Link>

            <Link to="/rooms" className="room-card">
              <div className="room-card-media">
                <img
                  src={HOME_ROOM_CARDS[3].src}
                  srcSet={HOME_ROOM_CARDS[3].srcSet}
                  sizes={ROOM_CARD_SIZES}
                  alt={HOME_ROOM_CARDS[3].alt}
                  width={HOME_ROOM_CARDS[3].width}
                  height={HOME_ROOM_CARDS[3].height}
                  loading="lazy"
                  decoding="async"
                />
                <span className="room-card-tag">Hill View</span>
              </div>
              <div className="room-card-body">
                <h3>Golpotori</h3>
                <p>A spacious room with wide views across the ridges.</p>
                <span className="room-card-meta">Sleeps 4 · Panoramic view</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 2. Experience — justify the choice after seeing rooms */}
      <section className="home-hallmarks">
        <div className="home-hallmarks-inner">
          <div className="home-section-head">
            <span className="eyebrow center">The Damtua experience</span>
            <h2>Considered in every detail.</h2>
            <p className="home-section-sub">
              Six quiet hallmarks that define a stay with us.
            </p>
          </div>

          <div className="hallmarks-grid">
            <article className="hallmark">
              <span className="hallmark-num">01</span>
              <HiOutlineSun className="hallmark-icon" aria-hidden="true" />
              <h3>Hill-line vistas</h3>
              <p>Rooms angled toward the ridges so each morning opens with mist and birdsong.</p>
            </article>
            <article className="hallmark">
              <span className="hallmark-num">02</span>
              <HiOutlineSparkles className="hallmark-icon" aria-hidden="true" />
              <h3>Regional cookery</h3>
              <p>Local Bengali and indigenous hill dishes prepared from neighbourhood gardens.</p>
            </article>
            <article className="hallmark">
              <span className="hallmark-num">03</span>
              <HiOutlineHome className="hallmark-icon" aria-hidden="true" />
              <h3>Quiet rooms</h3>
              <p>Eight intimate rooms, air-conditioned or naturally cooled by the breeze.</p>
            </article>
            <article className="hallmark">
              <span className="hallmark-num">04</span>
              <HiOutlineUsers className="hallmark-icon" aria-hidden="true" />
              <h3>Gathering pavilion</h3>
              <p>A community hall for weddings, parties, and meaningful gatherings.</p>
            </article>
            <article className="hallmark">
              <span className="hallmark-num">05</span>
              <HiOutlineMap className="hallmark-icon" aria-hidden="true" />
              <h3>Land of waterfalls</h3>
              <p>Guided walks to nearby falls and trails through the surrounding forest.</p>
            </article>
            <article className="hallmark">
              <span className="hallmark-num">06</span>
              <HiOutlineGlobe className="hallmark-icon" aria-hidden="true" />
              <h3>Quiet stewardship</h3>
              <p>A warm, twenty-four hour team — present without intrusion.</p>
            </article>
          </div>
        </div>
      </section>

      {/* 3. Dining — complete the stay experience */}
      <section className="home-dining">
        <div className="home-dining-grid">
          <div className="home-dining-media">
            <img
              src="/images/restaurant-5-sm.webp"
              alt="Damtua Inn restaurant dining room"
              loading="lazy"
              decoding="async"
              width="1200"
              height="800"
            />
          </div>
          <div className="home-dining-content">
            <span className="eyebrow">The Restaurant</span>
            <h2>Cooking grounded in the hills.</h2>
            <p>
              An honest, regional menu — bamboo chicken from the forest, local biriyani,
              vuna braised low, breads pulled in-house each morning. We cook close to the
              earth, in season, with care.
            </p>

            <div className="dining-menu">
              <div className="menu-row">
                <span className="menu-name">Bamboo Chicken</span>
                <span className="menu-dots" aria-hidden="true" />
                <span className="menu-price">৳ 1,200</span>
              </div>
              <div className="menu-row">
                <span className="menu-name">Local Chicken Biriyani</span>
                <span className="menu-dots" aria-hidden="true" />
                <span className="menu-price">৳ 315</span>
              </div>
              <div className="menu-row">
                <span className="menu-name">Beef Vuna</span>
                <span className="menu-dots" aria-hidden="true" />
                <span className="menu-price">৳ 315</span>
              </div>
              <div className="menu-row">
                <span className="menu-name">Chicken Vuna</span>
                <span className="menu-dots" aria-hidden="true" />
                <span className="menu-price">৳ 315</span>
              </div>
              <div className="menu-row">
                <span className="menu-name">Mixed Fried Rice</span>
                <span className="menu-dots" aria-hidden="true" />
                <span className="menu-price">৳ 370</span>
              </div>
              <div className="menu-row">
                <span className="menu-name">Chicken Roast</span>
                <span className="menu-dots" aria-hidden="true" />
                <span className="menu-price">৳ 260</span>
              </div>
            </div>

            <Link to="/restaurant" className="text-link">
              The full menu <FiArrowUpRight />
            </Link>
          </div>
        </div>
      </section>

      {/* 4. Services — remove friction, add confidence */}
      <Services />

      {/* 5. Our story — emotional trust for engaged scrollers */}
      <section className="home-intro">
        <div className="home-intro-grid">
          <div className="home-intro-media">
            <img
              src="/images/restaurant-1-sm.webp"
              alt="Damtua Inn nestled in the hills of Alikadam"
              loading="lazy"
              decoding="async"
              width="1200"
              height="800"
            />
            <div className="home-intro-meta">
              <span className="eyebrow">Damtua Inn</span>
              <p className="home-intro-label">Hill Retreat — Bandarban</p>
            </div>
          </div>
          <div className="home-intro-body">
            <h2 className="home-intro-title">
              A quiet inn shaped by the slow rhythm of the
              <em> hill tracts</em>.
            </h2>
            <p className="home-intro-paragraph">
              Set among the misted ridges of Alikadam, Damtua Inn offers a measured,
              soulful retreat — eight thoughtful rooms, an unhurried restaurant rooted in
              regional cookery, and a community pavilion held by the surrounding forest.
            </p>
            <p className="home-intro-paragraph">
              Whether you arrive for a weekend, a wedding, or simply a long breath of
              mountain air, the inn is built around one belief: that good hospitality
              is, above all, a kind of listening.
            </p>
            <Link to="/about" className="text-link">
              Our story <FiArrowUpRight />
            </Link>
          </div>
        </div>
      </section>

      {/* 6. Location — practical reassurance before booking */}
      <section className="home-find">
        <div className="home-find-inner">
          <div className="home-find-head">
            <span className="eyebrow">Visit</span>
            <h2>Finding Damtua Inn.</h2>
            <p className="home-section-sub">
              Tucked into the hills of Alikadam, ten minutes from the bus station by tomtom.
            </p>
          </div>

          <div className="home-find-grid">
            <div className="home-find-map">
              <LocationMap />
            </div>

            <aside className="home-find-card">
              <div className="find-block">
                <p className="find-label"><FiMapPin /> Address</p>
                <p className="find-value">
                  Damtua, Alikadam<br />
                  Bandarban, Chittagong Division<br />
                  Bangladesh — 4650
                </p>
                <p className="find-coords">21.6503° N · 92.3060° E</p>
              </div>

              <div className="find-block">
                <p className="find-label"><FiPhone /> Reservations</p>
                <p className="find-value">
                  Hotel <a href="tel:+8801748912127">+880 1748 912 127</a><br />
                  Restaurant <a href="tel:+8801883535200">+880 1883 535 200</a><br />
                  General <a href="tel:+8801879917302">+880 1879 917 302</a>
                </p>
              </div>

              <div className="find-block">
                <p className="find-label"><FiNavigation /> Arrival</p>
                <p className="find-value">
                  From Alikadam Bus Station, take a tomtom (local auto-rickshaw) and ask
                  for Damtua Hotel — ten to twenty taka per person.
                </p>
              </div>

              <a
                href="https://maps.app.goo.gl/SNBGwZmw6UttUV7G8"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline find-cta"
              >
                Open in Google Maps
                <FiArrowUpRight />
              </a>
            </aside>
          </div>
        </div>
      </section>

      {/* 7. Final conversion — close the decision */}
      <section className="home-cta">
        <div className="home-cta-inner">
          <span className="eyebrow center home-cta-eyebrow">Stay With Us</span>
          <h2 className="home-cta-title">
            The hills are <em>waiting</em>.
          </h2>
          <p className="home-cta-text">
            Reserve a room, plan a celebration, or simply come for the quiet.
          </p>
          <div className="home-cta-actions">
            <Link to="/booking" className="btn btn-on-dark">
              Reserve Now
            </Link>
            <Link to="/rooms" className="btn btn-outline-on-dark">
              Browse Rooms
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
